<template>
  <div id="topline">
    <topline-info-top :geo-type="geoType" />
    <topline-table
      :data="selectedGeoResults"
      :condensed="toplineParams.condensed"
      :geo-selection="geoSelection"
      :election-type="results.electionType"
      :party="results.party"
      :office-id="results.office_id"
      :footer-data="footerData"
      :show-map-legend="toplineParams.mapType !== ''"
      :show-precincts="showPrecincts"
      :params="toplineParams"
      :last-updated="lastUpdated" />

    <div
      v-if="selectedGeoResults.advancing_candidates?.length > 0"
      class="mt-2 flex items-center">
      <svg
        class="mr-1 h-5 w-5"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="#666666">
        <circle
          cx="10"
          cy="10"
          r="6"
          fill="white" />
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
          clip-rule="evenodd" />
      </svg>
      <span class="text-sm">{{ candidateAdvancingText }}</span>
    </div>
    <topline-info-bottom
      :geo-type="geoType"
      :geo-selection="geoSelection" />
  </div>
</template>

<script>
import {
  ToplineInfoBottom,
  ToplineInfoTop,
  ToplineTable,
  ToplineTableSelect,
} from "./toplineWidgets";
import { capitalize } from "@/filters";
import { mapGetters } from "vuex";

const components = {
  ToplineTable,
  ToplineTableSelect,
  ToplineInfoTop,
  ToplineInfoBottom,
};

const props = {
  results: {
    type: Object,
    required: true,
  },
  geoType: {
    type: String,
    required: false,
    default: "county",
  },
  geoData: {
    type: Array,
    required: false,
  },
  condensed: {
    type: Boolean,
    required: false,
    default: false,
  },
  lastUpdated: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    required: true,
  },
  footerData: {
    type: Object,
    required: true,
  },
  showPrecincts: {
    type: Boolean,
    required: false,
    default: false,
  },
  toplineParams: {
    type: Object,
    required: true,
  },
};

const advancingToGeneralRaces = [
  22539, 22542, 22551, 22593, 22595, 22599, 22600, 22601, 22602, 22603, 22604, 22606, 22609, 26141,
  26142, 26143, 26144, 26145, 26146, 26147, 26148, 26149, 26150, 26151, 26152, 26153, 26154, 26155,
  26156, 26157, 26158, 26159, 26160, 26161, 26162, 26163, 26164, 26165, 26166, 26167, 26168, 26169,
  26170, 26171, 26172, 26173, 26174, 26175, 26176, 26177, 26178, 26179, 26180, 26181, 26182, 26183,
  26184, 26185, 26186, 26187, 26188, 26189, 26190, 26191, 26192, 26193, 26194, 26195, 26196, 26197,
  26198, 26199, 26200, 26201, 26202, 26203, 26204, 26205, 26206, 26207, 26208, 26209, 26210, 26211,
  26212, 26213, 26214, 26215, 26216, 26217, 26218, 26219, 26220, 26221, 26222, 26223, 26224, 26225,
  26226, 26227, 26228, 26229, 26230, 26231, 26232, 26233, 26234, 26235, 26236, 26237, 26238, 26239,
  26240, 26241, 26242, 26243, 26244, 26245, 26246, 26247, 26248, 26249, 26250, 26251, 26252, 26253,
  26254, 26255, 26256, 26257, 26258, 26259, 26260, 26261, 26262, 26263, 26264, 26265, 26266, 26267,
  26268, 26269, 26270, 26271, 26272, 26273, 26274, 26275, 26276, 26277, 26278, 26279, 26280, 26281,
  26282, 26283, 26284, 26285, 26286, 26287, 26288, 26289, 26290, 26291, 26292, 26295, 26296, 26662,
  26663, 26664, 26665, 26666, 26667, 50752, 50754, 50759, 50760, 50763, 50764,
];

const computed = {
  ...mapGetters(["params"]),
  geoLabel() {
    // TODO: probably move this to parse file
    let geoLabel = "county";
    if (this.geoType === "vcu") {
      geoLabel = "township";
    }
    if (this.results.state_name === "Louisiana") {
      geoLabel = "parish";
    }
    return capitalize(geoLabel);
  },
  geoSelection() {
    return this.$store.getters.geoSelection;
  },
  selectedGeoResults() {
    if (typeof this.geoSelection === "undefined" || this.geoSelection === null || !this.geoData) {
      return this.results;
    }

    const res = this.results.counties.find((d) => {
      const name = d[this.geoType] ?? d["vcu"] ?? "";

      return name.toLowerCase() === this.geoSelection.toLowerCase();
    });

    return res;
  },
  isLA() {
    return this.results.state === "LA" && this.results.race_date === "2023-10-14";
  },
  candidateAdvancingText() {
    const advancingToGeneral = advancingToGeneralRaces.includes(this.results.race_id) || this.isLA;

    if (advancingToGeneral) return ` = ${this.$t("message.candidateAdvancingToGeneral")}`;

    return ` = ${this.$t("message.candidateAdvancingToRunoff")}`;
  },
};

export default {
  components,
  props,
  computed,
};
</script>
